// Job service - Real API calls to backend
import { apiClient } from './api';
import { API_ENDPOINTS } from '../utils/constants';
import mockJobs from '../data/mockJobs.json';
import mockMatches from '../data/mockMatches.json';

// Simulate API delay
const delay = (ms = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Get all jobs (for admin or employer viewing their own jobs)
export const getJobs = async (filters = {}) => {
  await delay();
  
  let jobs = [...mockJobs];
  
  // Apply filters
  if (filters.employer_id) {
    jobs = jobs.filter(job => job.employer_id === filters.employer_id);
  }
  
  if (filters.status) {
    jobs = jobs.filter(job => job.status === filters.status);
  }
  
  if (filters.job_role) {
    jobs = jobs.filter(job => job.job_role === filters.job_role);
  }
  
  if (filters.date_from) {
    jobs = jobs.filter(job => job.job_date >= filters.date_from);
  }
  
  if (filters.date_to) {
    jobs = jobs.filter(job => job.job_date <= filters.date_to);
  }
  
  return jobs;
};

// Get job by ID
export const getJobById = async (jobId) => {
  await delay();
  
  const job = mockJobs.find(j => j.id === parseInt(jobId));
  
  if (!job) {
    throw new Error('Job not found');
  }
  
  return job;
};

// Post a new job (employer) - Real API
export const postJob = async (jobData) => {
  // POST /api/job-postings/
  // Format: {
  //   "date": "2026-01-12",
  //   "job_role_id": 0,
  //   "number_of_hours": 1,
  //   "start_time": "13:55:22.366Z",
  //   "end_time": "13:55:22.366Z"
  // }
  
  // Convert time format from HH:MM to HH:MM:SS.sssZ format
  const formatTimeToISO = (timeStr) => {
    if (!timeStr) return null;

    if (timeStr.includes(':') && timeStr.split(':').length === 3) {
      return timeStr; // already HH:MM:SS
    }

    const [hours, minutes] = timeStr.split(':');
    if (!hours || !minutes) return null;

    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}:00`;
  };
  
  const requestData = {
    date: jobData.job_date || jobData.date,
    job_role_id: jobData.job_role_id,
    number_of_hours: jobData.number_of_hours || jobData.total_hours,
    start_time: formatTimeToISO(jobData.start_time),
    end_time: formatTimeToISO(jobData.end_time),
  };
  
  // Add break times if provided (optional fields)
  if (jobData.break_start_time) {
    requestData.break_start_time = formatTimeToISO(jobData.break_start_time);
  }
  if (jobData.break_end_time) {
    requestData.break_end_time = formatTimeToISO(jobData.break_end_time);
  }
  
  return apiClient.post(API_ENDPOINTS.JOB_POSTINGS.LIST, requestData);
};

// Get matches for current user
export const getMatches = async (userId, userRole) => {
  await delay();
  
  let matches = [...mockMatches];
  
  if (userRole === 'employee') {
    matches = matches.filter(m => m.employee_id === userId);
  } else if (userRole === 'employer') {
    matches = matches.filter(m => m.job.employer_id === userId);
  }
  
  return matches;
};

// Get match by ID (real API)
export const getMatchById = async (matchId) => {
  // GET /api/job-matches/{id}/
  return apiClient.get(`/job-matches/${matchId}/`);
};

// Accept a match (real API - only employees can accept)
export const acceptMatch = async (matchId, userRole) => {
  // Only employees can accept matches - backend handles auto-confirmation
  if (userRole !== 'employee') {
    throw new Error('Only employees can accept job matches.');
  }
  
  // POST /api/job-matches/{id}/accept/
  // Backend automatically:
  // - Sets match status to 'accepted'
  // - Updates job posting status to 'accepted'
  // - Marks availability as unavailable
  // - Discards other pending matches for the same job
  // - Sends notifications to both employee and employer
  const response = await apiClient.post(`/job-matches/${matchId}/accept/`);
  return response.match || response; // Backend returns { detail, match }
};

// Reject a match (real API - only employees can reject)
export const rejectMatch = async (matchId, userRole) => {
  // Only employees can reject matches
  if (userRole !== 'employee') {
    throw new Error('Only employees can reject job matches.');
  }
  
  // POST /api/job-matches/{id}/reject/
  const response = await apiClient.post(`/job-matches/${matchId}/reject/`);
  return response;
};

// Get potential matches for a job (employer view)
export const getJobMatches = async (jobId) => {
  await delay();
  
  return mockMatches.filter(m => m.job_id === parseInt(jobId));
};

// Get potential jobs for an employee
export const getPotentialJobs = async (employeeId) => {
  await delay();
  
  // This would normally filter by employee availability, role, and distance
  // For now, return all posted jobs
  return mockJobs.filter(job => job.status === 'posted');
};

// Get job postings for an employer (real API)
export const getEmployerJobPostings = async (employerId) => {
  const params = new URLSearchParams();
  if (employerId) {
    params.append('employer_id', employerId);
  }
  const queryString = params.toString();
  const endpoint = queryString ? `${API_ENDPOINTS.JOB_POSTINGS.LIST}?${queryString}` : API_ENDPOINTS.JOB_POSTINGS.LIST;
  return apiClient.get(endpoint);
};

// Get job matches for an employee (real API)
export const getEmployeeJobMatches = async () => {
  // Use the job-matches endpoint which automatically filters by current user (employee)
  // The backend filters matches based on user role - employees see their own matches
  // Endpoint: /api/job-matches/
  return apiClient.get('/job-matches/');
};

// Delete a job posting (real API - only employers can delete their own postings)
export const deleteJobPosting = async (jobPostingId) => {
  // DELETE /api/job-postings/{id}/
  // Backend ensures only employers can delete their own postings
  return apiClient.delete(API_ENDPOINTS.JOB_POSTINGS.BY_ID(jobPostingId));
};











